import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import { doc, updateDoc, onSnapshot, getDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { months } from '../utils/dateUtils';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import PageLayout from './PageLayout';
import { getContrastText } from '../utils/colorUtils';
import { soundService } from '../services/soundService';
import { usePlan } from '../contexts/PlanContext';

///Cards de estatisticas
function StatCard({ icon, title, value, color = 'primary', customColors }) {
 return (
    <Card sx={{ 
      p: { xs: 1, sm: 2 }, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      borderRadius: 2,
      boxShadow: 2,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)'
      }
    }}>
      <Box sx={{ 
        color: customColors?.primary || color, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { 
          sx: { fontSize: { xs: 32, sm: 40 } }
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="div" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          lineHeight: 1.2,
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h6" 
        sx={{ 
          color: customColors?.primary || 'primary.main',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 'bold'
        }}
      >
        {value}
      </Typography>
    </Card>
  );
}

// Novo componente para o diálogo de comparação de planos
function PlanComparisonDialog({ open, onClose, customColors }) {
  const theme = useTheme();
  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const primaryTextColor = getContrastText(primaryColor);

  const planFeatures = {
    basic: [
      { text: 'Cadastrar até 3 hábitos', limit: '3' },
      { text: 'Cadastrar até 2 metas', limit: '2' },
      { text: 'Cadastrar até 1 desafio', limit: '1' },
      { text: 'Cadastrar até 3 notas', limit: '3' },
      { text: 'Acesso à aba Produtividade', available: false },
      { text: 'Acesso à aba Meditações', available: false },
      { text: 'Acesso à aba Estatísticas', available: false }
    ],
    pro: [
      { text: 'Hábitos ilimitados', unlimited: true },
      { text: 'Metas ilimitadas', unlimited: true },
      { text: 'Desafios ilimitados', unlimited: true },
      { text: 'Notas ilimitadas', unlimited: true },
      { text: 'Acesso à aba Produtividade', available: true },
      { text: 'Acesso à aba Meditações', available: true },
      { text: 'Acesso à aba Estatísticas', available: true }
    ]
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        bgcolor: primaryColor,
        color: primaryTextColor,
        py: 2
      }}>
        Limite do Plano Básico Atingido
      </DialogTitle>
      <DialogContent sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Coluna do Plano Básico */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%', 
              position: 'relative',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                color: primaryColor,
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano Básico
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.basic.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {feature.available === false ? (
                      <CloseIcon color="error" />
                    ) : (
                      <CheckIcon sx={{ color: primaryColor }} />
                    )}
                    <Typography color="text.primary">
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Coluna do Plano PRO */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%',
              position: 'relative',
              bgcolor: primaryColor,
              color: primaryTextColor,
              '& .MuiTypography-root': {
                color: primaryTextColor
              }
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano PRO
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.pro.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon sx={{ color: primaryTextColor }} />
                    <Typography>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        justifyContent: 'center',
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            mr: 2,
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryColor,
              bgcolor: `${primaryColor}10`
            }
          }}
        >
          Continuar no Plano Básico
        </Button>
        <Button 
          variant="contained"
          sx={{ 
            bgcolor: primaryColor,
            color: primaryTextColor,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? `${primaryColor}dd`
                : `${primaryColor}bb`
            }
          }}
        >
          Fazer Upgrade para PRO
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Goals({ habits = [], customColors }) {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [goals, setGoals] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const { userPlan } = usePlan();
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    deadline: null,
    subtasks: [],
    completed: false,
    createdAt: null
  });
  const [newSubtask, setNewSubtask] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [soundSettings, setSoundSettings] = useState(null);

  const calculateProgress = (goal) => {
    if (goal.completed) return 100;
    if (!goal.subtasks.length) return 0;
    const completedSubtasks = goal.subtasks.filter(subtask => subtask.completed).length;
    return Math.round((completedSubtasks / goal.subtasks.length) * 100);
  };

  const stats = React.useMemo(() => {
    const total = goals.length;
    const completed = goals.filter(g => g.completed).length;
    const inProgress = goals.filter(g => {
      if (g.completed) return false;
      const progress = calculateProgress(g);
      return progress > 0 && progress < 100;
    }).length;
    const overdue = goals.filter(g => {
      if (g.completed) return false;
      if (!g.deadline) return false;
      const today = new Date();
      const deadlineDate = new Date(g.deadline);
      return deadlineDate < today;
    }).length;

    return {
      total,
      completed,
      inProgress,
      overdue
    };
  }, [goals]);

  // Carregar metas do Firebase quando o componente montar
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setGoals(userData.goals || []);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    loadSoundSettings();
  }, []);

  const loadSoundSettings = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.soundSettings) {
          setSoundSettings(userData.soundSettings);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de som:', error);
    }
  };

  // Função para salvar metas no Firebase
  const saveGoalsToFirebase = async (updatedGoals) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { 
        goals: updatedGoals,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Erro ao salvar metas:', error);
    }
  };

  const handleOpenDialog = () => {
    // Verifica se o usuário está no plano básico e já tem 2 metas
    if (userPlan === 'BASIC' && goals.length >= 2) {
      setShowPlanDialog(true);
      return;
    }
    setOpenDialog(true);
  };

  // Método estático para abrir o diálogo de nova meta
  Goals.onAdd = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGoal(null);
    setNewGoal({
      title: '',
      description: '',
      deadline: null,
      subtasks: [],
      completed: false,
      createdAt: null
    });
  };

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setNewGoal(prev => ({
        ...prev,
        subtasks: [...prev.subtasks, { text: newSubtask, completed: false }]
      }));
      setNewSubtask('');
    }
  };

  const handleRemoveSubtask = (index) => {
    setNewGoal(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index)
    }));
  };

  const handleSaveGoal = async () => {
    // Verifica se o usuário está no plano básico e já tem 2 metas
    if (userPlan === 'BASIC') {
      const currentGoalsCount = editingGoal 
        ? goals.filter(g => g.id !== editingGoal.id).length 
        : goals.length;
        
      if (currentGoalsCount >= 2) {
        setShowPlanDialog(true);
        setOpenDialog(false); // Fecha o diálogo de edição
        return;
      }
    }

    let updatedGoals;
    if (editingGoal) {
      updatedGoals = goals.map(goal => 
        goal.id === editingGoal.id ? { 
          ...newGoal, 
          id: editingGoal.id,
          createdAt: editingGoal.createdAt || new Date().toISOString()
        } : goal
      );
    } else {
      updatedGoals = [...goals, { 
        ...newGoal, 
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      }];
    }
    await saveGoalsToFirebase(updatedGoals);
    setGoals(updatedGoals);
    handleCloseDialog();
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setNewGoal(goal);
    setOpenDialog(true);
  };

  const handleDeleteGoal = async (goalId) => {
    const updatedGoals = goals.filter(goal => goal.id !== goalId);
    await saveGoalsToFirebase(updatedGoals);
    setGoals(updatedGoals);
  };

  const handleToggleSubtask = async (goalId, subtaskIndex) => {
    const updatedGoals = goals.map(goal => {
      if (goal.id === goalId) {
        const newSubtasks = [...goal.subtasks];
        const wasCompleted = newSubtasks[subtaskIndex].completed;
        newSubtasks[subtaskIndex] = {
          ...newSubtasks[subtaskIndex],
          completed: !wasCompleted
        };

        // Verifica se todas as subtarefas estão concluídas
        const allSubtasksCompleted = newSubtasks.every(subtask => subtask.completed);

        // Se todas as subtarefas foram concluídas e a meta não estava concluída antes
        const wasGoalCompleted = goal.completed;
        const newGoal = { 
          ...goal, 
          subtasks: newSubtasks,
          completed: allSubtasksCompleted
        };

        // Toca o som se a tarefa foi marcada como completa (não quando desmarcada)
        if (!wasCompleted) {
          soundService.playTaskComplete();
        }

        // Se a meta foi concluída agora (não estava antes), toca o som
        if (allSubtasksCompleted && !wasGoalCompleted) {
          soundService.playGoalComplete();
        }

        return newGoal;
      }
      return goal;
    });
    await saveGoalsToFirebase(updatedGoals);
    setGoals(updatedGoals);
  };

  const calculateDeadlineStatus = (deadline, progress) => {
    // Se o progresso for 100%, retorna status de concluída
    if (progress === 100) {
      return {
        status: 'Concluída',
        color: 'success',
        text: 'Meta alcançada'
      };
    }

    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffTime < 0) {
      return {
        status: 'Em atraso',
        color: 'error',
        text: `${Math.abs(diffDays)} dias em atraso`
      };
    } else {
      return {
        status: 'No prazo',
        color: 'success',
        text: `Faltam ${diffDays} dias`
      };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleToggleGoal = async (goalId) => {
    const updatedGoals = goals.map(goal => {
      if (goal.id === goalId) {
        return { ...goal, completed: !goal.completed };
      }
      return goal;
    });
    await saveGoalsToFirebase(updatedGoals);
    setGoals(updatedGoals);
  };

  const handleDeleteClick = async (goalId) => {
    const goalToDelete = goals.find(g => g.id === goalId);
    setGoalToDelete(goalToDelete);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (goalToDelete) {
      if (goalToDelete.title === 'todas as metas') {
        // Remover todas as metas
        await saveGoalsToFirebase([]);
        setGoals([]);
      } else {
        // Remover uma meta específica
        await handleDeleteGoal(goalToDelete.id);
      }
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setGoalToDelete(null);
  };

  const getFilteredGoals = () => {
    let filteredGoals;
    const today = new Date();
    switch (currentFilter) {
      case 'inProgress':
        filteredGoals = goals.filter(g => {
          if (g.completed) return false;
          // Em andamento: não concluída, prazo tanto em dia quanto atrasado
          return true;
        });
        break;
      case 'completed':
        filteredGoals = goals.filter(g => g.completed);
        break;
      case 'overdue':
        filteredGoals = goals.filter(g => {
          if (g.completed) return false;
          if (!g.deadline) return false;
          const deadlineDate = new Date(g.deadline);
          return deadlineDate < today;
        });
        break;
      case 'onTime':
        filteredGoals = goals.filter(g => {
          if (g.completed) return false;
          if (!g.deadline) return false;
          const deadlineDate = new Date(g.deadline);
          return deadlineDate >= today;
        });
        break;
      default:
        filteredGoals = goals;
    }
    // Ordenar por data de criação (do mais antigo para o mais recente)
    return filteredGoals.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateA - dateB;
    });
  };

  const handleFilterChange = (event, newValue) => {
    setCurrentFilter(newValue);
  };

  const handleToggleComplete = async (goal) => {
    try {
      const wasCompleted = goal.completed;
      const updatedGoal = {
        ...goal,
        completed: !goal.completed
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        goals: arrayRemove(goal)
      });
      await updateDoc(userRef, {
        goals: arrayUnion(updatedGoal)
      });

      setGoals(prev => 
        prev.map(g => g.id === goal.id ? updatedGoal : g)
      );

      // Toca o som apenas quando a meta é concluída (não quando é desmarcada)
      if (!wasCompleted && updatedGoal.completed) {
        soundService.playGoalComplete();
      }
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
    }
  };

  const handleToggleTask = async (goalId, taskId) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      if (!goal) return;

      const updatedTasks = goal.tasks.map(task =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      );

      // Verifica se a tarefa foi marcada como completa
      const task = goal.tasks.find(t => t.id === taskId);
      const wasCompleted = task.completed;
      const isNowCompleted = !wasCompleted;

      const updatedGoal = {
        ...goal,
        tasks: updatedTasks,
        completed: updatedTasks.every(task => task.completed)
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        goals: arrayRemove(goal)
      });
      await updateDoc(userRef, {
        goals: arrayUnion(updatedGoal)
      });

      // Toca o som se a tarefa foi marcada como completa (não quando desmarcada)
      if (isNowCompleted) {
        soundService.playTaskComplete();
      }

      // Se todas as tarefas foram completadas agora, toca o som de meta completa
      if (updatedGoal.completed && !goal.completed) {
        soundService.playGoalComplete();
      }

      setGoals(prev =>
        prev.map(g => g.id === goalId ? updatedGoal : g)
      );
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  return (
    <PageLayout
      title="Metas"
      onAdd={handleOpenDialog}
      customColors={customColors}
      actionButtons={
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          '& .MuiButton-root': {
            flex: { xs: 1, sm: 'initial' },
            minWidth: { xs: 0, sm: 'auto' },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '1rem' }
          }
        }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setGoalToDelete({ title: 'todas as metas' });
              setDeleteDialogOpen(true);
            }}
          >
            Remover Todos
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Nova Meta
          </Button>
        </Box>
      }
    > 
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<AssignmentIcon />}
              title="Total de Metas"
              value={goals.length}
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<CheckCircleIcon />}
              title="Metas Concluídas"
              value={goals.filter(goal => goal.completed).length}
              color="success"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Em Andamento"
              value={goals.filter(goal => !goal.completed && calculateProgress(goal) > 0).length}
              color="info"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            
            <StatCard
              icon={<WarningIcon />}
              title="Em Atraso"
              value={goals.filter(goal => {
                const status = calculateDeadlineStatus(goal.deadline, calculateProgress(goal));
                return status?.status === 'Em atraso' && !goal.completed;
              }).length}
              color="error"
              customColors={customColors}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={currentFilter}
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 1,
              alignItems: 'center'
            },
            '& .MuiTab-root': {
              borderRadius: 1,
              minHeight: 'auto',
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2,
              py: 1
            },
            '& .MuiTabs-indicator': {
              backgroundColor: customColors?.primary || 'primary.main'
            },
            '& .Mui-selected': {
              color: `${customColors?.primary || 'primary.main'} !important`
            }
          }}
        >
          <Tab label="Todas" value="all" />
          <Tab label="Em Andamento" value="inProgress" />
          <Tab label="Concluídas" value="completed" />
          {/* Separador visual */}
          <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 28, alignSelf: 'center' }} />
          <Tab label="Em Atraso" value="overdue" />
          <Tab label="No Prazo" value="onTime" />
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {getFilteredGoals().map((goal) => {
          const progress = calculateProgress(goal);
          const deadlineStatus = calculateDeadlineStatus(goal.deadline, progress);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={goal.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    fontSize: '1rem',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.2
                  }}>
                    {goal.title}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {/* Etiqueta principal: Em andamento ou Concluída */}
                    {goal.completed ? (
                      <Chip
                        label="Concluída"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 'normal' }}
                      />
                    ) : (
                      <Chip
                        label="Em andamento"
                        size="small"
                        sx={{
                          bgcolor: customColors?.primary || 'warning.main',
                          color: getContrastText(customColors?.primary || '#FFD600'),
                          fontWeight: 'normal',
                          border: 0
                        }}
                      />
                    )}
                    {/* Etiqueta secundária: Em atraso ou No prazo */}
                    {(() => {
                      if (!goal.completed && goal.deadline) {
                        const today = new Date();
                        const deadlineDate = new Date(goal.deadline);
                        const diffTime = deadlineDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        if (diffTime < 0) {
                          return (
                            <Chip
                              label={`Em atraso (${Math.abs(diffDays)} dia${Math.abs(diffDays) > 1 ? 's' : ''} em atraso)`}
                              color="error"
                              size="small"
                              sx={{ fontWeight: 'normal' }}
                            />
                          );
                        } else {
                          return (
                            <Chip
                              label={`No prazo (${diffDays} dia${diffDays > 1 ? 's' : ''} restante${diffDays > 1 ? 's' : ''})`}
                              size="small"
                              sx={{
                                bgcolor: '#FFD600',
                                color: getContrastText('#FFD600'),
                                fontWeight: 'normal',
                                border: 0
                              }}
                            />
                          );
                        }
                      }
                      return null;
                    })()}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {goal.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Criada em: {formatDate(goal.createdAt ? goal.createdAt.split('T')[0] : new Date().toISOString().split('T')[0])}
                    </Typography>
                    {goal.deadline && (
                      <Typography variant="body2" color="text.secondary">
                        Prazo: {formatDate(goal.deadline)}
                      </Typography>
                    )}
                  </Box>

                  {goal.subtasks.length > 0 && (
                    <List dense sx={{ mt: 1, mb: 2, p: 0 }}>
                      {goal.subtasks.map((subtask, index) => (
                        <ListItem 
                          key={index} 
                          dense 
                          disableGutters
                          sx={{
                            px: 0,
                            '&:last-child': {
                              pb: 0
                            }
                          }}
                        >
                          <Checkbox
                            checked={subtask.completed}
                            onChange={() => handleToggleSubtask(goal.id, index)}
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                            size="small"
                            sx={{ 
                              p: 0.5,
                              mr: 1,
                              color: customColors?.primary || 'primary.main',
                              '&.Mui-checked': {
                                color: customColors?.primary || 'primary.main'
                              }
                            }}
                          />
                          <ListItemText
                            primary={subtask.text}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: {
                                textDecoration: subtask.completed ? 'line-through' : 'none',
                                color: subtask.completed ? 'text.disabled' : 'text.primary',
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(customColors?.primary || 'primary.main', 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: customColors?.primary || 'primary.main',
                          borderRadius: 4
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                      {progress}%
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ 
                  justifyContent: 'center',
                  gap: 1,
                  p: 1,
                  borderTop: 1,
                  borderColor: 'divider'
                }}>
                  <Tooltip title="Concluir">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleComplete(goal)}
                      sx={{
                        color: progress === 100 ? "success.main" : (customColors?.primary || 'primary.main')
                      }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => handleEditGoal(goal)}
                      sx={{
                        color: customColors?.primary || 'primary.main'
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(goal.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dialog para adicionar/editar meta */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Editar Meta' : 'Nova Meta'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Título"
              fullWidth
              value={newGoal.title}
              onChange={(e) => setNewGoal(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={newGoal.description}
              onChange={(e) => setNewGoal(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Prazo"
              type="date"
              fullWidth
              value={newGoal.deadline}
              onChange={(e) => setNewGoal(prev => ({ ...prev, deadline: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  backgroundColor: customColors?.primary || 'primary.main',
                  backgroundImage: 'none',
                  maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }
              }}
            />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Subtarefas
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="Nova subtarefa"
                  fullWidth
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                />
                <Button
                  variant="contained"
                  onClick={handleAddSubtask}
                  sx={{
                    bgcolor: customColors?.primary || 'primary.main',
                    color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                    '&:hover': {
                      bgcolor: customColors?.primary || 'primary.main',
                      opacity: 0.9
                    }
                  }}
                >
                  Adicionar
                </Button>
              </Box>
              <List dense>
                {newGoal.subtasks.map((subtask, index) => (
                  <ListItem key={index}>
                    <Checkbox
                      checked={subtask.completed}
                      onChange={(e) => {
                        const updatedSubtasks = [...newGoal.subtasks];
                        updatedSubtasks[index] = { ...subtask, completed: e.target.checked };
                        setNewGoal(prev => ({ ...prev, subtasks: updatedSubtasks }));
                      }}
                      sx={{
                        color: customColors?.primary || 'primary.main',
                        '&.Mui-checked': {
                          color: customColors?.primary || 'primary.main'
                        }
                      }}
                    />
                    <ListItemText primary={subtask.text} />
                    <IconButton onClick={() => handleRemoveSubtask(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveGoal}
            variant="contained"
            sx={{ 
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {goalToDelete?.title === 'todas as metas' 
              ? "Tem certeza que deseja remover todas as metas? Esta ação não pode ser desfeita."
              : `Tem certeza que deseja excluir a meta "${goalToDelete?.title}"? Esta ação não pode ser desfeita.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Comparison Dialog */}
      <PlanComparisonDialog 
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        customColors={customColors}
      />
    </PageLayout>
  );
} 